import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	FormHelperText,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { PassType } from "../models/PassType";

interface FormErrors {
	Name?: string;
}

interface PassTypeDetailViewerProps {
	passType: PassType | null;
	existingPassTypes: PassType[];
	isSaving: boolean;
	onSave: (passType: PassType) => void;
	onClose: () => void;
}

export const PassTypeDetailViewer = ({
	passType,
	existingPassTypes,
	isSaving,
	onSave,
	onClose,
}: PassTypeDetailViewerProps) => {
	const [formData, setFormData] = useState<PassType | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewPassType = !passType?.Id;

	// Validate form and return errors
	const validateForm = useMemo(() => {
		return (data: PassType | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				// Check for duplicate name (case-insensitive)
				const isDuplicate = existingPassTypes.some(
					(pt) =>
						pt.Id !== data.Id &&
						pt.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			return newErrors;
		};
	}, [existingPassTypes]);

	// Check if form is valid
	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;

	// Check if new pass type can be created
	const canCreatePassType = isNewPassType && isFormValid;

	useEffect(() => {
		if (passType) {
			setFormData(sanitizeNullStrings({ ...passType }));
		} else {
			setFormData(null);
		}
		// Reset validation state when passType changes
		setErrors({});
		setTouched({});
	}, [passType]);

	if (!passType) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a pass type to view</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	// Handle field change (updates local state)
	const handleChange = (field: keyof PassType, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	// Mark field as touched and show error if any
	const handleBlur = (field: keyof PassType) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		// Update errors for touched field
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	// Auto-save for existing pass types (called on blur for text fields)
	const handleBlurSave = (field: keyof PassType, value: unknown) => {
		handleBlur(field);

		if (isNewPassType) return; // Don't auto-save new pass types

		// Check for validation errors on the field
		const currentErrors = validateForm(
			formData ? { ...formData, [field]: value } : null,
		);
		if (currentErrors[field as keyof FormErrors]) {
			return; // Don't save if there are validation errors
		}

		// Check if value actually changed
		const originalValue = passType[field];
		const normalizedOriginal = originalValue === null ? "" : originalValue;
		const normalizedNew = value === null ? "" : value;

		if (normalizedOriginal !== normalizedNew) {
			const updatedPassType = { ...formData, [field]: value };
			onSave(updatedPassType);
		}
	};

	// Auto-save for checkboxes (immediate on change)
	const handleCheckboxChange = (field: keyof PassType, value: unknown) => {
		handleChange(field, value);

		if (isNewPassType) return; // Don't auto-save new pass types

		// Check if value actually changed
		if (passType[field] !== value) {
			const updatedPassType = { ...formData, [field]: value };
			onSave(updatedPassType);
		}
	};

	// Create new pass type (explicit save)
	const handleCreate = () => {
		// Mark all fields as touched to show errors
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreatePassType) {
			onSave(formData);
		}
	};

	const sectionHeaderSx = {
		color: "primary.main",
		fontWeight: 600,
		fontSize: "0.85rem",
		mt: 1.5,
		mb: 0.5,
	};

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNewPassType ? "New Pass Type" : formData.Name}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{/* CREATE button only for new pass types */}
					{isNewPassType && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreatePassType || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					{/* CLOSE button always visible */}
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} disabled={isSaving}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1 }} />

			{/* Form Fields */}
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Basic Information</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Name"
						value={formData.Name}
						onChange={(e) => handleChange("Name", e.target.value)}
						onBlur={(e) => handleBlurSave("Name", e.target.value)}
						fullWidth
						size="small"
						required
						error={touched.Name && !!errors.Name}
						helperText={touched.Name && errors.Name}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Description"
						value={formData.Description ?? ""}
						onChange={(e) => handleChange("Description", e.target.value)}
						onBlur={(e) => handleBlurSave("Description", e.target.value)}
						fullWidth
						size="small"
						multiline
						rows={3}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={formData.Active}
								onChange={(e) =>
									handleCheckboxChange("Active", e.target.checked)
								}
								size="small"
							/>
						}
						label="Active"
						sx={{ ml: 0 }}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
